import { useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { Button } from '@/shared/components/ui/button';
import { Field, FieldError, FieldLabel, FieldDescription } from '@/shared/components/ui/field';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select';
import { FormFieldInput } from '@/shared/components/custom/form-field/FormFieldInput';
import { FormField } from '@/shared/components/custom/form-field/FormField';
import { PillRadioGroup } from '@/shared/components/custom/pill-radio-group';
import { FormFieldTextarea } from '@/shared/components/custom/form-field/FormFieldTextarea';
import { UserAvatar } from '@/shared/components/custom/avatars/UserAvatar';
import { getUserColor } from '@/core/lib/colors';
import { useMembers } from '@/features/members/public';
import { useTimeOffTypes } from '../../hooks/useTimeOffTypes';
import { useCreateHistoricalTimeOffRequest } from '../../hooks/useCreateHistoricalTimeOffRequest';
import {
  historicalRequestSchema,
  type HistoricalRequestFormInput,
  type HistoricalRequestFormValues,
} from '../../schemas';
import { DateRangeWithDailyTimeField } from '../requests/DateRangeWithDailyTimeField';

const MEMBERS_PAGE_SIZE = 200;

interface HistoricalRequestFormProps {
  onSuccess?: () => void;
  onCancel?: () => void;
}

/**
 * Single historical-request registration — Owner/Admin only. Identifies the
 * member by email (per the confirmed `api.json` shape for
 * `CreateHistoricalTimeOffRequestRequest`) — the member picker below resolves
 * the selected member's email under the hood, so the form still submits an
 * email, just chosen from a list instead of typed.
 */
export function HistoricalRequestForm({ onSuccess, onCancel }: HistoricalRequestFormProps) {
  const { t } = useTranslation('timeOff');
  const { data: types } = useTimeOffTypes();
  const { data: members } = useMembers('', 0, MEMBERS_PAGE_SIZE);
  const createHistorical = useCreateHistoricalTimeOffRequest();
  const [succeeded, setSucceeded] = useState(false);

  const { control, handleSubmit, reset } = useForm<
    HistoricalRequestFormInput,
    unknown,
    HistoricalRequestFormValues
  >({
    resolver: zodResolver(historicalRequestSchema(t)),
    defaultValues: { userEmail: '', typeCode: '', cycleNumber: undefined, days: [], reason: '' },
  });

  function onSubmit(values: HistoricalRequestFormValues) {
    createHistorical.mutate(
      {
        userEmail: values.userEmail,
        typeCode: values.typeCode,
        cycleNumber: values.cycleNumber,
        days: values.days.map((day) => ({
          date: day.date,
          startTime: day.isFullDay ? undefined : day.startTime,
          endTime: day.isFullDay ? undefined : day.endTime,
        })),
        reason: values.reason || undefined,
      },
      {
        onSuccess: () => {
          setSucceeded(true);
          onSuccess?.();
        },
      },
    );
  }

  if (succeeded) {
    return (
      <div className="space-y-4 p-6" data-testid="time-off-historical-form-success">
        <p className="text-sm font-medium text-foreground">
          {t('team.balances.registerDialog.success')}
        </p>
        <Button
          variant="outline"
          onClick={() => {
            setSucceeded(false);
            reset();
          }}
        >
          {t('team.balances.registerDialog.createAnother')}
        </Button>
      </div>
    );
  }

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className="flex min-h-0 flex-1 flex-col"
      data-testid="time-off-historical-form"
    >
      <div className="min-h-0 flex-1 space-y-4 overflow-y-auto p-6">
        <Controller
          name="userEmail"
          control={control}
          render={({ field, fieldState }) => (
            <Field data-invalid={fieldState.invalid}>
              <FieldLabel>{t('team.balances.registerDialog.member')}</FieldLabel>
              <Select value={field.value} onValueChange={field.onChange}>
                <SelectTrigger
                  id="historical-member"
                  aria-invalid={fieldState.invalid}
                  data-testid="time-off-historical-form-member"
                >
                  <SelectValue placeholder={t('team.balances.registerDialog.memberPlaceholder')} />
                </SelectTrigger>
                <SelectContent>
                  {(members ?? []).map((member) => (
                    <SelectItem key={member.userId} value={member.email}>
                      <UserAvatar
                        name={member.displayName}
                        color={getUserColor(member.userId)}
                        size="xs"
                      />
                      {member.displayName}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {fieldState.invalid && <FieldError errors={[fieldState.error]} />}
            </Field>
          )}
        />

        <FormField
          control={control}
          name="typeCode"
          label={t('team.balances.registerDialog.typeCode')}
          render={({ field, invalid, disabled }) => (
            <PillRadioGroup
              id="typeCode"
              value={field.value as string}
              onValueChange={field.onChange}
              options={(types ?? []).map((type) => ({
                value: type.code ?? '',
                label: type.name ?? '',
              }))}
              disabled={disabled}
              aria-invalid={invalid}
              data-testid="time-off-historical-form-type"
            />
          )}
        />

        <div>
          <FormFieldInput
            control={control}
            name="cycleNumber"
            type="number"
            label={t('team.balances.registerDialog.cycleNumber')}
            data-testid="time-off-historical-form-cycle-number"
          />
          <FieldDescription>{t('team.balances.registerDialog.cycleNumberHelp')}</FieldDescription>
        </div>

        <Controller
          name="days"
          control={control}
          render={({ field, fieldState }) => (
            <Field data-invalid={fieldState.invalid}>
              <FieldLabel>{t('requests.form.dateRange')}</FieldLabel>
              <DateRangeWithDailyTimeField value={field.value ?? []} onChange={field.onChange} />
              {fieldState.invalid && <FieldError errors={[fieldState.error]} />}
            </Field>
          )}
        />

        <FormFieldTextarea
          control={control}
          name="reason"
          label={t('team.balances.registerDialog.reason')}
          data-testid="time-off-historical-form-reason"
        />

        <p
          className="rounded-md bg-primary/5 p-3 text-xs text-primary"
          data-testid="time-off-historical-form-helper-note"
        >
          {t('team.balances.registerDialog.helperNote')}
        </p>
      </div>

      <div className="flex shrink-0 justify-end gap-2 border-t p-6">
        {onCancel && (
          <Button type="button" variant="outline" onClick={onCancel}>
            {t('team.balances.registerDialog.cancel')}
          </Button>
        )}
        <Button
          type="submit"
          disabled={createHistorical.isPending}
          data-testid="time-off-historical-form-submit"
        >
          {t('team.balances.registerDialog.submit')}
        </Button>
      </div>
    </form>
  );
}
